// src/reporting/pdf/addStackedBarChart.js

import dayjs from "lib/dayjs";
import { PDF_CONTENT_GAP, addSectionTitle, ensurePageSpace } from "./pdfLayout";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatKmb(value) {
  const n = Number(value) || 0;
  const a = Math.abs(n);

  if (a >= 1e9) return `${(n / 1e9).toFixed(a >= 10e9 ? 0 : 1)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 10e6 ? 0 : 1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 10e3 ? 0 : 1)}K`;

  return `${Math.round(n)}`;
}

function formatDateLabel(value) {
  const d = dayjs(value);
  if (!d.isValid()) return String(value || "");
  return d.format("M/YY");
}

function niceMax(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const exponent = Math.floor(Math.log10(n));
  const magnitude = 10 ** exponent;
  const normalized = n / magnitude;

  let nice = 10;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;

  return nice * magnitude;
}

function buildTicks(maxValue, tickCount = 5) {
  const max = niceMax(maxValue);
  const step = max / Math.max(1, tickCount - 1);

  return Array.from({ length: tickCount }, (_, index) => step * index);
}

function buildLegendLayout(ctx, series, maxWidth) {
  const { doc } = ctx;
  const columnCount = maxWidth >= 300 ? 3 : maxWidth >= 180 ? 2 : 1;
  const columnGap = 10;
  const itemWidth =
    (maxWidth - columnGap * (columnCount - 1)) / Math.max(1, columnCount);
  const textWidth = Math.max(30, itemWidth - 16);
  const lineHeight = 8;
  const rowGap = 7;
  const topPadding = 2;
  const bottomPadding = 12;
  const rows = [];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  series.forEach((s, index) => {
    const label = String(s.label || s.key || "");
    const lines = doc.splitTextToSize(label, textWidth);
    const item = {
      color: s.color,
      lines,
      column: index % columnCount,
      height: Math.max(12, lines.length * lineHeight + 3),
    };

    if (item.column === 0) rows.push([]);

    rows[rows.length - 1].push(item);
  });

  const rowHeights = rows.map((row) =>
    Math.max(...row.map((item) => item.height), 10)
  );
  const height =
    topPadding +
    rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0) +
    Math.max(0, rowHeights.length - 1) * rowGap +
    bottomPadding;

  return {
    columnCount,
    columnGap,
    itemWidth,
    lineHeight,
    rowGap,
    topPadding,
    rows,
    rowHeights,
    height,
  };
}

function addLegend(ctx, layout, x, y) {
  const { doc } = ctx;
  let cy = y + layout.topPadding;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(70);

  layout.rows.forEach((row, rowIndex) => {
    row.forEach((item) => {
      const cx = x + item.column * (layout.itemWidth + layout.columnGap);

      doc.setFillColor(...item.color);
      doc.rect(cx, cy + 1, 10, 6, "F");

      doc.text(item.lines, cx + 14, cy + 6, {
        lineHeightFactor: layout.lineHeight / 7,
      });
    });

    cy += layout.rowHeights[rowIndex] + layout.rowGap;
  });
}

export function addStackedBarChart(ctx, config = {}, startY) {
  const { doc, margins, usableWidth } = ctx;
  const {
    title = "Chart",
    rows = [],
    series = [],
    valuePrefix = "$",
    dateKey = "date",
    height = 220,
    legend = true,
  } = config;

  const safeRows = Array.isArray(rows) ? rows : [];
  const safeSeries = Array.isArray(series) ? series : [];

  const chartX = margins.left;
  const chartW = usableWidth;
  const plotLeft = chartX + 42;
  const plotRight = chartX + chartW - 10;
  const plotW = plotRight - plotLeft;
  const legendLayout =
    legend && safeSeries.length ? buildLegendLayout(ctx, safeSeries, plotW) : null;
  const legendHeight = legendLayout?.height || 0;
  const frameHeight = height + Math.max(0, legendHeight - 22);
  const neededHeight = frameHeight + 48;
  let y = ensurePageSpace(ctx, startY, neededHeight);

  y = addSectionTitle(ctx, title, y);

  if (!safeRows.length || !safeSeries.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("No chart data available.", margins.left, y);
    return y + PDF_CONTENT_GAP;
  }

  const chartY = y;
  const plotTop = chartY + 8;
  const plotBottom =
    chartY + frameHeight - (legend ? legendHeight + 24 : 24);
  const plotH = plotBottom - plotTop;

  const maxValue = Math.max(
    ...safeRows.map((row) =>
      safeSeries.reduce((sum, s) => sum + Math.max(0, num(row[s.key])), 0)
    ),
    1
  );
  const ticks = buildTicks(maxValue, 5);
  const yMax = ticks[ticks.length - 1] || 1;
  const yPos = (value) => plotBottom - (Math.max(0, value) / yMax) * plotH;
  const barSlot = safeRows.length > 0 ? plotW / safeRows.length : plotW;
  const barW = Math.max(2, Math.min(14, barSlot * 0.58));
  const xPos = (index) => plotLeft + barSlot * index + barSlot / 2;

  doc.setDrawColor(215);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(chartX, chartY, chartW, frameHeight, 5, 5, "S");

  ticks.forEach((tick) => {
    const yy = yPos(tick);

    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.35);
    doc.line(plotLeft, yy, plotRight, yy);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(85);
    doc.text(`${valuePrefix}${formatKmb(tick)}`, plotLeft - 7, yy + 2.5, {
      align: "right",
    });
  });

  doc.setDrawColor(130, 130, 130);
  doc.setLineWidth(0.6);
  doc.line(plotLeft, plotBottom, plotRight, plotBottom);
  doc.line(plotLeft, plotTop, plotLeft, plotBottom);

  safeRows.forEach((row, index) => {
    const x = xPos(index);
    const barX = x - barW / 2;
    let running = 0;

    safeSeries.forEach((s) => {
      const value = Math.max(0, num(row[s.key]));
      if (value <= 0) return;

      const y0 = yPos(running);
      running += value;
      const y1 = yPos(running);

      doc.setFillColor(...s.color);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.rect(barX, y1, barW, y0 - y1, "FD");
    });
  });

  const labelEvery = Math.max(1, Math.ceil(safeRows.length / 8));
  safeRows.forEach((row, index) => {
    if (index % labelEvery !== 0 && index !== safeRows.length - 1) return;

    const x = xPos(index);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(85);
    doc.text(formatDateLabel(row[dateKey]), x, plotBottom + 12, {
      align: "center",
    });
  });

  if (legend) {
    addLegend(ctx, legendLayout, plotLeft, plotBottom + 30);
  }

  return chartY + frameHeight + PDF_CONTENT_GAP;
}

export default addStackedBarChart;
